import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { IconColor } from '@salvapp/ui-components';
import { SalvButtonVariant } from '@salvapp/ui-components/build/SalvButton/SalvButton.types';
import { ReactNode } from 'react';
import { Dispatch } from 'redux';

export interface ConfirmationModalState {
  isOpen: boolean;
  title: string;
  description?: ReactNode;
  icon?: IconDefinition;
  iconColor?: IconColor;
  confirmButtonVariant?: SalvButtonVariant;
  confirmationText?: string;
  onConfirm?: () => void;
  cancelText?: string;
  onCancel?: () => void;
}

const initialState: ConfirmationModalState = {
  isOpen: false,
  title: '',
};

type ShowConfirmationModalProps = Omit<ConfirmationModalState, 'isOpen'>;

const confirmationModalSlice = createSlice({
  name: 'confirmationModal',
  initialState,
  reducers: {
    PRIVATE_showConfirmationModal: (state, action: PayloadAction<ShowConfirmationModalProps>) => {
      const { payload } = action;
      state.isOpen = true;
      state.title = payload.title ?? initialState.title;
      state.description = payload.description ?? initialState.description;
      state.icon = payload.icon ?? initialState.icon;
      state.iconColor = payload.iconColor ?? initialState.iconColor;
      state.confirmButtonVariant = payload.confirmButtonVariant ?? initialState.confirmButtonVariant;
      state.confirmationText = payload.confirmationText ?? initialState.confirmationText;
      state.onConfirm = payload.onConfirm ?? initialState.onConfirm;
      state.cancelText = payload.cancelText ?? initialState.cancelText;
      state.onCancel = payload.onCancel ?? initialState.onCancel;
    },
    hideConfirmationModal: (state) => {
      state.isOpen = false;
    },
  },
});

export const showConfirmationModal =
  (
    props: Omit<ShowConfirmationModalProps, 'onConfirm'> & {
      onConfirm?: (() => void) | null;
    }
  ) =>
  async (dispatch: Dispatch): Promise<boolean> => {
    return new Promise((resolve) => {
      dispatch(
        PRIVATE_showConfirmationModal({
          ...props,
          onConfirm:
            props.onConfirm !== null
              ? () => {
                  props.onConfirm?.();
                  resolve(true);
                }
              : undefined,
          onCancel: () => {
            props.onCancel?.();
            resolve(false);
          },
        })
      );
    });
  };

// Extract the action creators object and the reducer
const { actions, reducer } = confirmationModalSlice;
// Extract and export each action creator by name
export const { hideConfirmationModal, PRIVATE_showConfirmationModal } = actions;
// Export the reducer, either as a default or named export
export default reducer;
